import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Paperclip, X } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { formatDate } from '../lib/format';
import {
  formatBytes,
  MAX_TICKET_FILE_BYTES,
  MAX_TICKET_FILES,
  TICKET_FILE_ACCEPT,
  TICKET_FILE_EXTENSIONS,
  ticketFileKind,
  ticketFileUrl,
  uploadTicketFile,
  type TicketFile,
} from '../lib/ticket-files';
import type { WorkOrderDetail } from '../lib/types';
import { useToast } from './toast';
import { Card, CardHeader, cn, ConfirmDialog, Spinner } from './ui';

/**
 * A ticket's files: what someone attached in the app, and what came in on an email about it. Pictures are shown by the
 * pictures card; these are downloaded, so the list is names, sizes and who added them.
 */
export function TicketFilesCard({ ticket }: { ticket: WorkOrderDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<TicketFile | null>(null);
  const [dragging, setDragging] = useState(false);
  const canAdd = ticket.permissions.addFiles;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['work-order', ticket.id] });

  const add = useMutation({
    mutationFn: (files: File[]) => Promise.all(files.map((file) => uploadTicketFile(ticket.id, file))),
    onSuccess: (saved) => {
      refresh();
      toast.success(saved.length === 1 ? `Added ${saved[0].fileName}` : `Added ${saved.length} files`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (file: TicketFile) => api.delete(`/work-orders/${ticket.id}/files/${file.id}`),
    onSuccess: (_, file) => {
      refresh();
      setRemoving(null);
      toast.success(`Removed ${file.fileName}`);
    },
    onError: (err) => {
      setRemoving(null);
      toast.error(errorMessage(err));
    },
  });

  /** Keeps the obvious mistakes out of the upload: pictures, kinds a ticket doesn't take, and anything too big. */
  const choose = (chosen: FileList | File[] | null) => {
    const files = [...(chosen ?? [])];
    if (!files.length) return;
    const pictures = files.filter((file) => file.type.startsWith('image/'));
    const wrongKind = files.filter((file) => !pictures.includes(file) && !ticketFileKind(file.name));
    const tooBig = files.filter((file) => file.size > MAX_TICKET_FILE_BYTES);
    const room = MAX_TICKET_FILES - ticket.files.length;
    const ok = files.filter((file) => !pictures.includes(file) && !wrongKind.includes(file) && !tooBig.includes(file)).slice(0, Math.max(0, room));
    if (pictures.length) toast.error(pictures.length === 1 ? 'Add a picture under Pictures, so it shows on the ticket' : 'Add pictures under Pictures, so they show on the ticket');
    if (wrongKind.length) toast.error(`A ticket takes ${TICKET_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(', ')} files`);
    if (tooBig.length) toast.error(`Files can be up to ${Math.round(MAX_TICKET_FILE_BYTES / (1024 * 1024))} MB`);
    if (files.length - pictures.length - wrongKind.length - tooBig.length > ok.length) toast.error(`A ticket can have up to ${MAX_TICKET_FILES} files`);
    if (ok.length) add.mutate(ok);
  };

  return (
    <Card>
      <CardHeader
        title="Files"
        description={ticket.files.length > 0 ? `${ticket.files.length} of ${MAX_TICKET_FILES}` : 'PDFs, documents, spreadsheets and logs about this ticket.'}
        actions={
          canAdd ? (
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-within:ring-2 focus-within:ring-brand-500">
              {add.isPending ? <Spinner className="size-4" /> : <Paperclip className="size-4" />}
              {add.isPending ? 'Adding…' : 'Add a file'}
              <input
                type="file"
                accept={TICKET_FILE_ACCEPT}
                multiple
                className="sr-only"
                onChange={(event) => {
                  choose(event.target.files);
                  event.target.value = '';
                }}
              />
            </label>
          ) : undefined
        }
      />
      <div
        className={cn('p-5', dragging && canAdd && 'bg-brand-50/60 ring-2 ring-inset ring-brand-400')}
        onDragOver={(event) => {
          if (!canAdd || !event.dataTransfer.types.includes('Files')) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(event) => {
          if (!canAdd || !event.dataTransfer.types.includes('Files')) return;
          event.preventDefault();
          setDragging(false);
          choose(event.dataTransfer.files);
        }}
      >
        {ticket.files.length === 0 ? (
          <p className="text-sm text-slate-500">
            {canAdd ? 'Drop a file here, or choose Add a file. Files that come in on an email about this ticket land here too.' : 'No files yet.'}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {ticket.files.map((file) => (
              <li key={file.id} className="flex flex-wrap items-center gap-3 py-2 first:pt-0 last:pb-0">
                <FileText className="size-5 shrink-0 text-slate-400" />
                <a
                  href={ticketFileUrl(ticket.id, file.id)}
                  className="min-w-0 flex-1 truncate font-medium text-brand-700 hover:underline"
                  title={`Download ${file.fileName}`}
                >
                  {file.fileName}
                </a>
                <span className="text-xs whitespace-nowrap text-slate-500">
                  {formatBytes(file.sizeBytes)} · {file.uploadedByName} · {formatDate(file.createdAt)}
                </span>
                <a href={ticketFileUrl(ticket.id, file.id)} aria-label={`Download ${file.fileName}`} className="text-slate-400 hover:text-slate-700">
                  <Download className="size-4" />
                </a>
                {file.canDelete && (
                  <button
                    type="button"
                    aria-label={`Remove ${file.fileName}`}
                    className="text-slate-400 hover:text-red-700"
                    onClick={() => setRemoving(file)}
                  >
                    <X className="size-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {add.isPending && (
          <p className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <Spinner className="size-4" /> Uploading…
          </p>
        )}
      </div>

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
        loading={remove.isPending}
        tone="danger"
        title={removing ? `Remove ${removing.fileName}?` : ''}
        description="It comes off the ticket for everyone. The ticket keeps a note that it was removed."
        confirmLabel="Remove"
      />
    </Card>
  );
}
